import usePrivateAxios from '@/common/api/usePrivateAxios';
import { ErrorResponse } from '@/common/types/errorResponse';
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { DiagnosticObjectiveActivityValidationSchema } from '../../schemas/diagnosticObjectiveActivity.schema';
import { getRailsUpdate } from '@/common/utils/getRailsUpdate';
import { mapToDeletion } from '@/common/utils/railsHelpers';
import { DiagnosticActivityData } from '../../../../types/forwardings/directiveEntries/diagnosticEntry/diagnosticEntry.type';

interface UseUpdateDiagnosticObjectiveActivityProps {
  diagnosticObjectiveActivity: DiagnosticActivityData | undefined;
  options?: Partial<Omit<UseMutationOptions<
    unknown,
    ErrorResponse,
    DiagnosticObjectiveActivityValidationSchema
  >, 'mutationKey' | 'mutationFn'>>;
}

export const useUpdateDiagnosticObjectiveActivity = ({
  diagnosticObjectiveActivity,
  options,
}: UseUpdateDiagnosticObjectiveActivityProps) => {
  const privateAxios = usePrivateAxios();

  return useMutation({
    mutationKey: ['updateDiagnosticObjectiveActivity', diagnosticObjectiveActivity?.id],
    mutationFn: (form) => {
      const payload = {
        display_name: form.display_name,
        description: form.description,
        unit_price: form.unit_price && unFormatCurrency(form.unit_price),
        quantity: form.quantity && unFormatUnknownUnit(form.quantity),
        warranty_in_days: form.warranty_in_days && unFormatUnknownUnit(form.warranty_in_days),
        unit_for_quantity: form.unit_for_quantity?.value === 'hours'
          ? 'minutes'
          : form.unit_for_quantity?.value,
        technical_professional_ids: form.technical_professional.map(({ value }) => value),
        images_attachments_attributes: getRailsUpdate(
          diagnosticObjectiveActivity?.images || [],
          form.images_attachments_attributes || [],
          (x) => x.id,
          (y) => y.id,
          mapToDeletion,
          (z) => ({
            blob_id: z.id,
          }),
        ),
      }

      return privateAxios.put(`/maintenance/diagnostic_objective_activities/${diagnosticObjectiveActivity?.id}`, {
        ...payload,
      });
    },
    ...options
  });
}
